import { DateRangeSearch } from '@routes/__root'
import { applyTtlDateRangeAdjustment, getTtlDateRangeAdjustment } from '@routes/-enforceTtlDateRange'
import { protectWithWorkspacePermissions } from '@routes/-protectWithPermission'
import { withCompanyName } from '@routes/-withCompanyName'
import { createFileRoute, redirect } from '@tanstack/react-router'
import {
  botDetectionArraySearchParam,
  botDetectionCategorySearchParamSchema,
  botDetectionConfidenceSearchParamSchema,
  botDetectionIdentitySearchParamSchema,
} from 'features/botDetection'
import { fraudScoreFiltersSearchSchema, IdentificationVisitsPage } from 'features/identificationVisits'
import { EVENTS_EXCLUDED_DATE_RANGES, PREDEFINED_DATE_RANGE_IDS } from 'helpers/dateRange'
import { withCachedSearch } from 'helpers/withCachedSearch'
import { canViewAnalyticsPermission } from 'hooks/usePermission'
import { isEmpty } from 'lodash'
import { z } from 'zod/v4'

const eventsFilterSearchSchema = z.object({
  url: z.string().optional(),
  origin: z.string().optional(),
  asn: z.coerce.number().optional(),
  visitorId: z.coerce.string().optional(),
  highRecallId: z.coerce.string().optional(),
  ipAddress: z.string().optional(),
  proximityId: z.coerce.string().optional(),
  linkedId: z.coerce.string().optional(),
  environment: z
    .preprocess((val) => {
      if (typeof val === 'string') {
        return val.split(',')
      }
      return val
    }, z.array(z.string()))
    .optional(),
  sdkVersion: z.string().optional(),
  sdkPlatform: z.enum(['js', 'ios', 'android']).optional(),
  bot: z.enum(['all', 'good', 'bad', 'none']).optional(),
  vpn: z.coerce.boolean().optional(),
  torNode: z.coerce.boolean().optional(),
  virtualMachine: z.coerce.boolean().optional(),
  tampering: z.coerce.boolean().optional(),
  antiDetectBrowser: z.coerce.boolean().optional(),
  incognito: z.coerce.boolean().optional(),
  privacySettings: z.coerce.boolean().optional(),
  jailbroken: z.coerce.boolean().optional(),
  frida: z.coerce.boolean().optional(),
  factoryReset: z.coerce.boolean().optional(),
  clonedApp: z.coerce.boolean().optional(),
  emulator: z.coerce.boolean().optional(),
  simulator: z.coerce.boolean().optional(),
  rootApps: z.coerce.boolean().optional(),
  minSuspectScore: z.coerce.number().optional(),
  ipBlocklist: z.coerce.boolean().optional(),
  highActivity: z.coerce.boolean().optional(),
  proxy: z.coerce.boolean().optional(),
  developerTools: z.coerce.boolean().optional(),
  locationSpoofing: z.coerce.boolean().optional(),
  mitmAttack: z.coerce.boolean().optional(),
  deviceRarity: z.coerce.boolean().optional(),
  activeCall: z.coerce.boolean().optional(),
  botName: botDetectionArraySearchParam(z.string()),
  category: botDetectionArraySearchParam(botDetectionCategorySearchParamSchema),
  provider: botDetectionArraySearchParam(z.string()),
  identity: botDetectionArraySearchParam(botDetectionIdentitySearchParamSchema),
  confidence: botDetectionArraySearchParam(botDetectionConfidenceSearchParamSchema),
  ...fraudScoreFiltersSearchSchema.shape,
})

const cachedSearchParams = [
  'period',
  'before',
  'since',
  'url',
  'origin',
  'proximityId',
  'asn',
  'visitorId',
  'highRecallId',
  'ipAddress',
  'linkedId',
  'environment',
  'sdkVersion',
  'sdkPlatform',
  'bot',
  'vpn',
  'torNode',
  'virtualMachine',
  'tampering',
  'antiDetectBrowser',
  'incognito',
  'privacySettings',
  'jailbroken',
  'frida',
  'factoryReset',
  'clonedApp',
  'emulator',
  'simulator',
  'rootApps',
  'minSuspectScore',
  'ipBlocklist',
  'highActivity',
  'proxy',
  'developerTools',
  'locationSpoofing',
  'mitmAttack',
  'deviceRarity',
  'activeCall',
  'botName',
  'category',
  'provider',
  'identity',
  'confidence',
] as const

export type EventsFilterSearch = z.infer<typeof eventsFilterSearchSchema>
type CombinedSearch = EventsFilterSearch & DateRangeSearch
type CachedKeys = (typeof cachedSearchParams)[number]
type CachedSubset = Pick<CombinedSearch, CachedKeys>
type AllDateRangeIds = (typeof PREDEFINED_DATE_RANGE_IDS)[number]
export type SupportedDateRangeId = AllDateRangeIds

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/events/')({
  component: IdentificationVisitsPage,
  validateSearch: eventsFilterSearchSchema,
  head: () => ({
    meta: [
      {
        title: withCompanyName('Identification Events'),
      },
    ],
  }),
  beforeLoad: async (ctx): Promise<void | Response> => {
    await protectWithWorkspacePermissions(canViewAnalyticsPermission)(ctx)

    const { search, location, params, cause } = ctx

    if (cause !== 'preload') {
      const cachedSearch = withCachedSearch<CachedSubset>({
        search,
        matchedRoute: location.pathname,
        cachedParamsWhitelist: cachedSearchParams,
        searchStr: location.searchStr,
      })

      if (cachedSearch) {
        return redirect({
          to: '/workspaces/$subscriptionId/events',
          params,
          replace: true,
          search: (old) => ({
            ...old,
            ...cachedSearch,
          }),
        })
      }
    }
    const adjustedSearch = await getTtlDateRangeAdjustment(
      { ...ctx, search: search as Partial<DateRangeSearch> },
      { excludedIds: EVENTS_EXCLUDED_DATE_RANGES }
    )

    if (!isEmpty(adjustedSearch)) {
      return redirect({
        to: '/workspaces/$subscriptionId/events',
        params,
        replace: true,
        search: (old) => applyTtlDateRangeAdjustment(old, adjustedSearch),
      })
    }
  },
})
